/**
 * git-grasp-pool — GitGraspPool
 *
 * The single abstraction through which all git HTTP operations flow for a
 * given repository. Replaces the pattern where every hook and component
 * independently does Promise.any(cloneUrls.map(...)) fan-outs.
 *
 * Responsibilities:
 * - Own the URL racing strategy (race infoRefs, pick winner, fallback)
 * - Track per-URL state (connection status, latency, capabilities)
 * - Encapsulate CORS proxy logic
 * - Encapsulate the IDB/memory cache layer
 * - Expose observable state for UI
 * - Provide a simple API for consumers (getTree, getBlob, getCommitHistory, etc.)
 * - Accept new clone URLs dynamically (as announcement events arrive)
 * - Integrate with Nostr state events (backoff re-fetch, warning computation)
 */

import { BehaviorSubject, Subscription } from "rxjs";
import type { Observable } from "rxjs";
import type {
  PoolState,
  PoolHealth,
  PoolOptions,
  PoolSubscriber,
  PoolWarning,
  AuthoritativeRef,
  AuthoritativeHead,
  ResolvedRefMap,
  ViewSource,
  RefDiscrepancy,
  UrlRefStatus,
  StateEventInput,
  Commit,
  Tree,
  CommitRangeData,
  CommitComparisonData,
  InfoRefsUploadPackResponse,
} from "./types";
import { CorsProxyManager } from "./cors-proxy";
import { GitObjectCache } from "./cache";
import {
  GitHttpClient,
  classifyFetchError,
  classifyObjectFetchError,
  isNonHttpUrl,
} from "./git-http";
import { UrlStateManager, UrlTracker } from "./url-state";
import { PROVISIONING_BACKOFF_MAX_MS, StateEventManager } from "./state-event";
import {
  pushRefUpdateToGraspServers,
  type PushDeliverySummary,
} from "./grasp-push";
import type { NostrEvent } from "nostr-tools";
import type { PackableObject } from "@/lib/git-packfile";
import { ZERO_HASH, type RefUpdate } from "@/lib/git-push";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEFAULT_EVICTION_GRACE_MS = 60_000;

/**
 * How many commits to walk when verifying that the state head is an ancestor
 * of a git server head that claims to be ahead of the signed state.
 */
const ANCESTRY_VERIFICATION_MAX_DEPTH = 500;

/** Graph sizes probed before falling back to a caller's full comparison cap. */
const COMPARISON_GRAPH_DEPTHS = [15, 60, 240, 960];

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

function makeInitialState(): PoolState {
  return {
    urls: {},
    winnerUrl: null,
    health: "idle",
    loading: false,
    pulling: false,
    latestCommit: null,
    readmeContent: null,
    readmeFilename: null,
    defaultBranch: null,
    warning: null,
    authoritativeRefs: {},
    authoritativeHead: null,
    viewSource: "authoritative",
    effectiveRefs: {},
    error: null,
    lastCheckedAt: null,
    crossRefDiscrepancies: [],
    retryAt: null,
  };
}

function ancestryDistances(
  startHash: string,
  commits: Map<string, Commit>,
): Map<string, number> {
  const distances = new Map<string, number>();
  const queue: Array<{ hash: string; distance: number }> = [
    { hash: startHash, distance: 0 },
  ];

  while (queue.length > 0) {
    const next = queue.shift();
    if (!next) break;

    const existing = distances.get(next.hash);
    if (existing !== undefined && existing <= next.distance) continue;
    distances.set(next.hash, next.distance);

    const commit = commits.get(next.hash);
    if (!commit) continue;

    for (const parent of commit.parents) {
      queue.push({ hash: parent, distance: next.distance + 1 });
    }
  }

  return distances;
}

function findMergeBaseInHistories(
  commitA: string,
  commitB: string,
  chainA: Commit[],
  chainB: Commit[],
): string | null {
  const byHash = new Map([...chainA, ...chainB].map((c) => [c.hash, c]));
  const distancesFromA = ancestryDistances(commitA, byHash);
  const distancesFromB = ancestryDistances(commitB, byHash);

  let bestHash: string | null = null;
  let bestScore = Number.POSITIVE_INFINITY;

  for (const [hash, distanceA] of distancesFromA) {
    const distanceB = distancesFromB.get(hash);
    if (distanceB === undefined) continue;

    const score = distanceA + distanceB;
    if (score < bestScore) {
      bestScore = score;
      bestHash = hash;
    }
  }

  return bestHash;
}

function commitsUntilSharedHistory(
  tipCommitId: string,
  mergeBaseId: string,
  firstHistory: Commit[],
  secondHistory: Commit[],
): Commit[] | null {
  if (tipCommitId === mergeBaseId) return [];

  const commitsByHash = new Map(
    [...firstHistory, ...secondHistory].map((commit) => [commit.hash, commit]),
  );
  const secondHistoryHashes = new Set(
    secondHistory.map((commit) => commit.hash),
  );
  const sharedHashes = new Set(
    firstHistory
      .filter((commit) => secondHistoryHashes.has(commit.hash))
      .map((commit) => commit.hash),
  );
  sharedHashes.add(mergeBaseId);

  const rangeHashes = new Set<string>();
  const pending = [tipCommitId];

  while (pending.length > 0) {
    const commitId = pending.pop();
    if (!commitId || sharedHashes.has(commitId) || rangeHashes.has(commitId)) {
      continue;
    }

    const commit = commitsByHash.get(commitId);
    if (!commit) return null;
    rangeHashes.add(commitId);
    pending.push(...commit.parents);
  }

  return Array.from(rangeHashes)
    .map((hash) => commitsByHash.get(hash))
    .filter((commit): commit is Commit => !!commit)
    .sort(
      (left, right) =>
        (right.committer?.timestamp ?? right.author.timestamp) -
        (left.committer?.timestamp ?? left.author.timestamp),
    );
}

function comparisonGraphDepths(maxDepth: number): number[] {
  const depths = COMPARISON_GRAPH_DEPTHS.filter((depth) => depth < maxDepth);
  depths.push(maxDepth);
  return [...new Set(depths)];
}

function comparisonFromHistories(
  baseCommitId: string,
  headCommitId: string,
  baseHistory: Commit[],
  headHistory: Commit[],
): CommitComparisonData | null {
  const mergeBaseId = findMergeBaseInHistories(
    baseCommitId,
    headCommitId,
    baseHistory,
    headHistory,
  );
  if (!mergeBaseId) return null;

  const commitsByHash = new Map(
    [...baseHistory, ...headHistory].map((commit) => [commit.hash, commit]),
  );
  const baseCommit = commitsByHash.get(baseCommitId);
  const headCommit = commitsByHash.get(headCommitId);
  if (!baseCommit || !headCommit) return null;

  const baseRange = commitsUntilSharedHistory(
    baseCommitId,
    mergeBaseId,
    baseHistory,
    headHistory,
  );
  const headRange = commitsUntilSharedHistory(
    headCommitId,
    mergeBaseId,
    headHistory,
    baseHistory,
  );
  if (!baseRange || !headRange) return null;

  const baseHashes = new Set(baseRange.map((commit) => commit.hash));
  const headHashes = new Set(headRange.map((commit) => commit.hash));

  return {
    mergeBaseId,
    baseCommit,
    headCommit,
    baseOnlyCommits: baseRange.filter((commit) => !headHashes.has(commit.hash)),
    headOnlyCommits: headRange.filter((commit) => !baseHashes.has(commit.hash)),
  };
}

/**
 * Estimate the `deepen` depth needed for a shallow fetch from `tipCommitId` to
 * include every known commit in `tipCommitId..stopAtCommitId`.
 *
 * Walks the commit graph (via parent links) rather than relying on the
 * timestamp-sorted order of `history` — rebased or cherry-picked commits with
 * old committer dates, clock skew, and merge-heavy side branches all make a
 * timestamp index under-count the true graph depth. `deepen N` includes
 * commits whose minimum parent-distance from the tip is less than N, so the
 * required depth is the maximum such distance plus one. Over-estimating is
 * safe (the server ignores objects it already has); under-estimating produces
 * an incomplete pack.
 */
function estimateDeepenDepth(
  history: Commit[],
  tipCommitId: string,
  stopAtCommitId: string,
): number {
  const byHash = new Map(history.map((commit) => [commit.hash, commit]));
  const distances = ancestryDistances(tipCommitId, byHash);

  // The stop commit itself is excluded: the receiving side already has it.
  // Unknown parent hashes (beyond the walked history) are kept — they add one
  // level of safety margin when the history walk was truncated.
  let maxDistance = 0;
  for (const [hash, distance] of distances) {
    if (hash === stopAtCommitId) continue;
    maxDistance = Math.max(maxDistance, distance);
  }

  return Math.max(1, maxDistance + 1);
}

// ---------------------------------------------------------------------------
// Ref status computation helpers
// ---------------------------------------------------------------------------

/**
 * Determine whether two commit hashes are equivalent (one may be a prefix
 * of the other when abbreviated hashes are involved).
 */
function commitsMatch(a: string, b: string): boolean {
  return a === b || a.startsWith(b) || b.startsWith(a);
}

interface ServerRefCandidate {
  commitId: string;
  sourceUrl: string;
  rawTagOid?: string;
}

interface ServerRefCandidateGroup {
  commitId: string;
  members: ServerRefCandidate[];
}

interface RefAncestryVerification {
  stateCommit: string;
  gitCommit: string;
  status: "pending" | "complete" | "unavailable";
  descendsFromState: boolean | undefined;
  history: string[] | undefined;
  promise: Promise<void>;
}

/**
 * Compute per-URL, per-ref sync status and cross-ref discrepancies.
 *
 * For each ref that appears in at least one server's infoRefs:
 *   - If a state event exists: compare each server's commit against the
 *     state event's commit for that ref.
 *   - If no state event: compare each server's commit against the majority
 *     (first server that reported the ref).
 *
 * Returns:
 *   - `urlRefStatuses`: map of url → { refName → UrlRefStatus }
 *   - `urlRefCommits`:  map of url → { refName → commitHash }
 *   - `crossRefDiscrepancies`: refs where servers disagree
 */
function computeRefStatuses(
  trackers: UrlTracker[],
  stateEvent: StateEventInput,
  stateEose: boolean,
): {
  urlRefStatuses: Map<string, Record<string, UrlRefStatus>>;
  urlRefCommits: Map<string, Record<string, string>>;
  crossRefDiscrepancies: RefDiscrepancy[];
} {
  const urlRefStatuses = new Map<string, Record<string, UrlRefStatus>>();
  const urlRefCommits = new Map<string, Record<string, string>>();

  // Initialise maps for all trackers
  for (const t of trackers) {
    urlRefStatuses.set(t.url, {});
    urlRefCommits.set(t.url, {});
  }

  // Collect all ref names across all ok servers, excluding peeled tag entries
  // (refs/tags/foo^{}) — those are dereference helpers, not real refs.
  const allRefNames = new Set<string>();
  for (const t of trackers) {
    if (t.status === "ok" && t.state.infoRefs) {
      for (const refName of Object.keys(t.state.infoRefs.refs)) {
        if (!refName.endsWith("^{}")) {
          allRefNames.add(refName);
        }
      }
    }
  }

  const crossRefDiscrepancies: RefDiscrepancy[] = [];
  const hasStateEvent =
    stateEvent !== undefined && stateEvent !== null && stateEose;

  for (const refName of allRefNames) {
    // Build a map of url → commit for this ref (only ok servers).
    // For annotated tags, prefer the peeled commit hash (refName + "^{}")
    // because the state event stores the peeled commit, not the tag object.
    const peeledRefName = refName + "^{}";
    const serverCommits: Array<{
      url: string;
      commit: string;
      /** Raw tag object OID from infoRefs (only set for annotated tags) */
      rawTagOid?: string;
    }> = [];
    for (const t of trackers) {
      if (t.status === "ok" && t.state.infoRefs) {
        const rawOid = t.state.infoRefs.refs[refName];
        const peeledOid = t.state.infoRefs.refs[peeledRefName];
        // Use the peeled commit when available (annotated tag), otherwise the
        // raw ref value (lightweight tag or branch).
        const commit = peeledOid ?? rawOid;
        if (commit) {
          serverCommits.push({
            url: t.url,
            commit,
            // Only record rawTagOid when it differs from the peeled commit
            // (i.e. this is an annotated tag, not a lightweight tag/branch).
            rawTagOid: peeledOid && peeledOid !== rawOid ? rawOid : undefined,
          });
        }
      }
    }

    // Determine the "expected" commit for this ref
    let expectedCommit: string | undefined;
    if (hasStateEvent && stateEvent) {
      const stateRef = stateEvent.refs.find((r) => r.name === refName);
      expectedCommit = stateRef?.commitId;
    } else if (serverCommits.length > 0) {
      // No state event — use the first server's commit as the reference
      expectedCommit = serverCommits[0].commit;
    }

    // Assign per-URL status for this ref
    let disagreeCount = 0;
    for (const { url, commit, rawTagOid } of serverCommits) {
      const statusMap = urlRefStatuses.get(url)!;
      const commitMap = urlRefCommits.get(url)!;
      commitMap[refName] = commit;

      if (!expectedCommit) {
        // State event exists but doesn't mention this ref — can't compare
        statusMap[refName] = "connected";
      } else if (commitsMatch(commit, expectedCommit)) {
        statusMap[refName] = "match";
      } else if (rawTagOid && commitsMatch(rawTagOid, expectedCommit)) {
        // Old ngit (pre-fix) stored the tag object OID in the state event
        // instead of the peeled commit. The server's peeled commit differs
        // from the state's tag object OID, but the raw tag OID matches —
        // the server is consistent; treat this as a match to avoid false
        // discrepancy warnings for repos published by older ngit versions.
        statusMap[refName] = "match";
      } else {
        statusMap[refName] = "behind";
        disagreeCount++;
      }
    }

    // Cross-ref discrepancy: refs where servers disagree (2+ servers needed)
    if (serverCommits.length >= 2 && disagreeCount > 0) {
      crossRefDiscrepancies.push({
        refName,
        disagreeCount,
        totalServers: serverCommits.length,
        expectedCommit,
        servers: serverCommits.map(({ url, commit, rawTagOid }) => ({
          url,
          commit,
          matches: expectedCommit
            ? commitsMatch(commit, expectedCommit) ||
              (!!rawTagOid && commitsMatch(rawTagOid, expectedCommit))
            : true,
        })),
      });
    }
  }

  // For error/untested/permanent-failure URLs, set status based on connection
  for (const t of trackers) {
    const statusMap = urlRefStatuses.get(t.url)!;
    if (t.status === "permanent-failure" || t.status === "error") {
      // Mark all known refs as "error" for this URL
      for (const refName of allRefNames) {
        statusMap[refName] = "error";
      }
    } else if (t.status === "untested") {
      for (const refName of allRefNames) {
        statusMap[refName] = "unknown";
      }
    }
  }

  return { urlRefStatuses, urlRefCommits, crossRefDiscrepancies };
}

// ---------------------------------------------------------------------------
// GitGraspPool
// ---------------------------------------------------------------------------

export class GitGraspPool {
  // --- Internal services ---
  private cors: CorsProxyManager;
  /** The shared content-addressed object cache. Read-only access for consumers. */
  readonly cache: GitObjectCache;
  private http: GitHttpClient;
  private urlManager: UrlStateManager;
  private stateManager: StateEventManager;

  // --- Observable state ---
  private state$ = new BehaviorSubject<PoolState>(makeInitialState());

  // --- Subscriber management ---
  private subscribers = new Set<PoolSubscriber>();
  private evictTimer: ReturnType<typeof setTimeout> | null = null;
  private evictionGraceMs: number;

  // --- Fetch lifecycle ---
  private abort: AbortController | null = null;
  private fetching = false;
  private fetchedOnce = false;

  // --- State event subscription ---
  private stateEventSub: Subscription | null = null;

  // --- Winner tracking ---
  private winnerUrl: string | null = null;

  // --- Per-ref ancestry verification ---
  /**
   * Cached "is the server commit a descendant of the state commit?" results,
   * keyed by full ref name and guarded by the exact commit pair. `undefined`
   * means the check is in flight.
   */
  private ancestryVerifications = new Map<string, RefAncestryVerification>();

  constructor(options: PoolOptions) {
    this.evictionGraceMs =
      options.evictionGracePeriodMs ?? DEFAULT_EVICTION_GRACE_MS;

    // Initialize services
    this.cors = new CorsProxyManager(
      options.corsProxyBase,
      options.knownCorsBlockedOrigins,
    );
    this.cache = new GitObjectCache(options.infoRefsTtlMs);
    this.http = new GitHttpClient(
      this.cache,
      this.cors,
      options.expectRepositoryProvisioning,
    );
    this.urlManager = new UrlStateManager(this.cors);
    this.stateManager = new StateEventManager();
    if (options.expectRepositoryProvisioning) {
      this.stateManager.setMaxBackoffMs(PROVISIONING_BACKOFF_MAX_MS);
    }

    // Add initial URLs
    this.urlManager.addUrls(options.cloneUrls);

    // Subscribe to state event observable if provided
    if (options.stateEvent$) this.setStateEventSource(options.stateEvent$);
  }

  /**
   * Attach or replace the repository state event source for this pool.
   *
   * Pools are shared by clone URL, so the first consumer to create a pool may
   * not have repo-state context. Later consumers can provide it here without
   * forcing a separate pool for the same git server.
   */
  setStateEventSource(stateEvent$: Observable<StateEventInput>): void {
    this.stateEventSub?.unsubscribe();
    this.stateEventSub = stateEvent$.subscribe((stateEvent) => {
      this.onStateEventChange(stateEvent);
    });
  }

  /**
   * Configure the pool for a repository that GRASP is actively provisioning.
   * Pools are shared, so this may be enabled after an earlier consumer already
   * classified the not-yet-created endpoints.
   */
  setRepositoryProvisioningExpected(expected: boolean): void {
    this.http.setExpectRepositoryProvisioning(expected);
    this.stateManager.setMaxBackoffMs(
      expected ? PROVISIONING_BACKOFF_MAX_MS : undefined,
    );
    if (expected) this.urlManager.resetFailures();
  }

  // -----------------------------------------------------------------------
  // Observable state
  // -----------------------------------------------------------------------

  /** Get the current state as an RxJS observable */
  get observable(): Observable<PoolState> {
    return this.state$.asObservable();
  }

  /** Get the current state snapshot */
  getState(): PoolState {
    return this.state$.getValue();
  }

  // -----------------------------------------------------------------------
  // Subscriber management
  // -----------------------------------------------------------------------

  /**
   * Subscribe to pool state changes. Returns an unsubscribe function.
   * Triggers the initial fetch if not already started.
   */
  subscribe(cb: PoolSubscriber): () => void {
    // Cancel any pending eviction
    if (this.evictTimer !== null) {
      clearTimeout(this.evictTimer);
      this.evictTimer = null;
    }

    this.subscribers.add(cb);

    // Deliver current state immediately
    cb(this.state$.getValue());

    // Start fetching if we haven't yet
    if (!this.fetchedOnce && !this.fetching) {
      this.startFetch();
    }

    return () => {
      this.subscribers.delete(cb);
      if (this.subscribers.size === 0) {
        this.scheduleEviction();
      }
    };
  }

  private notify(): void {
    const state = this.state$.getValue();
    for (const cb of this.subscribers) {
      cb(state);
    }
  }

  private setState(
    updater: (prev: PoolState) => PoolState,
    verifyHead = true,
  ): void {
    const next = updater(this.state$.getValue());
    // Protocol truth and display preference are recomputed together on every
    // emission. The view layer can never feed back into authoritativeRefs.
    next.authoritativeRefs = this.deriveAuthoritativeRefs();
    next.authoritativeHead = this.deriveAuthoritativeHead(next);
    next.effectiveRefs = this.deriveEffectiveRefs(next);
    this.state$.next(next);
    this.notify();

    if (verifyHead) {
      const headRef = this.getHeadRef(next);
      if (headRef) void this.ensureRefVerification(headRef);
    }
  }

  // -----------------------------------------------------------------------
  // Authoritative ref resolution
  // -----------------------------------------------------------------------

  private getHeadRef(next: PoolState): string | undefined {
    const stateEvent = this.stateManager.currentState;
    if (stateEvent?.headRef) return stateEvent.headRef;
    const winnerInfo = this.getInfoRefs();
    const gitHeadRef = winnerInfo?.symrefs["HEAD"];
    if (gitHeadRef) return gitHeadRef;
    return next.defaultBranch ? `refs/heads/${next.defaultBranch}` : undefined;
  }

  private getServerRefCandidates(refName: string): ServerRefCandidate[] {
    const peeledName = `${refName}^{}`;
    const candidates: ServerRefCandidate[] = [];

    for (const tracker of this.urlManager.getAll()) {
      if (tracker.status !== "ok" || !tracker.state.infoRefs) continue;
      const refs = tracker.state.infoRefs.refs;
      const rawTagOid = refs[refName];
      const commitId = refs[peeledName] ?? rawTagOid;
      if (commitId) {
        candidates.push({
          commitId,
          sourceUrl: tracker.url,
          rawTagOid:
            refs[peeledName] && rawTagOid !== commitId ? rawTagOid : undefined,
        });
      }
    }

    return candidates;
  }

  private groupServerRefCandidates(refName: string): ServerRefCandidateGroup[] {
    const groups: ServerRefCandidateGroup[] = [];
    for (const candidate of this.getServerRefCandidates(refName)) {
      const group = groups.find((entry) =>
        commitsMatch(entry.commitId, candidate.commitId),
      );
      if (group) group.members.push(candidate);
      else groups.push({ commitId: candidate.commitId, members: [candidate] });
    }
    return groups;
  }

  private pickGroupSource(group: ServerRefCandidateGroup): ServerRefCandidate {
    return (
      group.members.find((member) => member.sourceUrl === this.winnerUrl) ??
      group.members[0]
    );
  }

  /**
   * Pick the server value for a ref that has no signed state entry. Majority
   * wins; ties prefer the pool winner, then stable URL-manager order.
   */
  private selectServerRefCandidate(refName: string): ServerRefCandidate | null {
    const groups = this.groupServerRefCandidates(refName);
    if (groups.length === 0) return null;
    groups.sort((a, b) => {
      if (a.members.length !== b.members.length) {
        return b.members.length - a.members.length;
      }
      const aHasWinner = a.members.some(
        (member) => member.sourceUrl === this.winnerUrl,
      );
      const bHasWinner = b.members.some(
        (member) => member.sourceUrl === this.winnerUrl,
      );
      return Number(bHasWinner) - Number(aHasWinner);
    });

    const selected = groups[0];
    return this.pickGroupSource(selected);
  }

  private verificationKey(
    refName: string,
    stateCommit: string,
    gitCommit: string,
  ): string {
    return `${refName}\u0000${stateCommit}\u0000${gitCommit}`;
  }

  /**
   * Resolve the commit that acts as the signed ancestry anchor. Old ngit
   * state events stored annotated-tag object IDs; when a server supplies the
   * corresponding peeled commit, use that commit for ancestry and tree reads.
   */
  private getStateRefAnchor(
    stateCommit: string,
    groups: ServerRefCandidateGroup[],
  ): string {
    for (const group of groups) {
      const matchingMember = group.members.find(
        (member) =>
          commitsMatch(member.commitId, stateCommit) ||
          (!!member.rawTagOid && commitsMatch(member.rawTagOid, stateCommit)),
      );
      if (matchingMember) return matchingMember.commitId;
    }
    return stateCommit;
  }

  /**
   * Choose among server commits already verified to descend from signed
   * state. A single candidate wins even when most mirrors remain at state.
   * With multiple candidates, only a tip containing every other candidate is
   * safe; incomparable descendants leave the signed state authoritative.
   */
  private selectVerifiedDescendant(
    refName: string,
    stateCommit: string,
    groups: ServerRefCandidateGroup[],
  ): ServerRefCandidate | null {
    const differing = groups.filter(
      (group) => !commitsMatch(group.commitId, stateCommit),
    );
    const withVerification = differing.map((group) => ({
      group,
      verification: this.ancestryVerifications.get(
        this.verificationKey(refName, stateCommit, group.commitId),
      ),
    }));
    if (
      withVerification.some(
        ({ verification }) =>
          !verification || verification.status === "pending",
      )
    ) {
      return null;
    }

    const verified = withVerification
      .filter(({ verification }) => verification?.descendsFromState === true)
      .map(({ group }) => group);
    if (verified.length === 0) return null;

    const containingTips = verified.filter((group) => {
      const verification = this.ancestryVerifications.get(
        this.verificationKey(refName, stateCommit, group.commitId),
      );
      return verified.every(
        (other) =>
          commitsMatch(group.commitId, other.commitId) ||
          verification?.history?.some((hash) =>
            commitsMatch(hash, other.commitId),
          ),
      );
    });
    if (containingTips.length === 0) return null;

    containingTips.sort((a, b) => {
      if (a.members.length !== b.members.length) {
        return b.members.length - a.members.length;
      }
      const aHasWinner = a.members.some(
        (member) => member.sourceUrl === this.winnerUrl,
      );
      const bHasWinner = b.members.some(
        (member) => member.sourceUrl === this.winnerUrl,
      );
      return Number(bHasWinner) - Number(aHasWinner);
    });
    return this.pickGroupSource(containingTips[0]);
  }

  private deriveAuthoritativeRefs(): ResolvedRefMap {
    const refNames = new Set<string>();
    const stateEvent = this.stateManager.currentState;
    if (stateEvent) {
      for (const ref of stateEvent.refs) refNames.add(ref.name);
    }
    for (const tracker of this.urlManager.getAll()) {
      if (tracker.status !== "ok" || !tracker.state.infoRefs) continue;
      for (const refName of Object.keys(tracker.state.infoRefs.refs)) {
        if (!refName.endsWith("^{}")) refNames.add(refName);
      }
    }

    const resolved: ResolvedRefMap = {};
    for (const refName of refNames) {
      const stateRef = stateEvent?.refs.find((ref) => ref.name === refName);
      if (!stateRef) {
        const server = this.selectServerRefCandidate(refName);
        if (server) {
          resolved[refName] = {
            commitId: server.commitId,
            source: "git",
            sourceUrl: server.sourceUrl,
          };
        }
        continue;
      }

      const groups = this.groupServerRefCandidates(refName);
      const stateAnchor = this.getStateRefAnchor(stateRef.commitId, groups);
      const verified = this.selectVerifiedDescendant(
        refName,
        stateAnchor,
        groups,
      );
      if (verified) {
        resolved[refName] = {
          commitId: verified.commitId,
          source: "git",
          sourceUrl: verified.sourceUrl,
        };
      } else {
        resolved[refName] = {
          commitId: stateAnchor,
          source: "state",
        };
      }
    }

    return resolved;
  }

  /**
   * Backward-compatible default-branch alias over authoritativeRefs.
   */
  private deriveAuthoritativeHead(next: PoolState): AuthoritativeHead | null {
    const stateEvent = this.stateManager.currentState;
    const headRef = this.getHeadRef(next);
    if (headRef && next.authoritativeRefs[headRef]) {
      return next.authoritativeRefs[headRef];
    }
    if (stateEvent?.headCommitId) {
      return { commitId: stateEvent.headCommitId, source: "state" };
    }
    return next.latestCommit
      ? {
          commitId: next.latestCommit.hash,
          source: "git",
          sourceUrl: next.winnerUrl ?? undefined,
        }
      : null;
  }

  private deriveEffectiveRefs(next: PoolState): ResolvedRefMap {
    if (next.viewSource === "authoritative") {
      return { ...next.authoritativeRefs };
    }

    const effective: ResolvedRefMap = { ...next.authoritativeRefs };
    const stateEvent = this.stateManager.currentState;
    if (next.viewSource === "nostr") {
      for (const ref of stateEvent?.refs ?? []) {
        const groups = this.groupServerRefCandidates(ref.name);
        effective[ref.name] = {
          commitId: this.getStateRefAnchor(ref.commitId, groups),
          source: "state",
        };
      }
      return effective;
    }

    const selectedUrl = next.viewSource;
    const tracker = this.urlManager.get(selectedUrl);
    if (!tracker?.state.infoRefs) return effective;
    for (const refName of Object.keys(tracker.state.infoRefs.refs)) {
      if (refName.endsWith("^{}")) continue;
      const commitId =
        tracker.state.infoRefs.refs[`${refName}^{}`] ??
        tracker.state.infoRefs.refs[refName];
      if (commitId) {
        effective[refName] = {
          commitId,
          source: "git",
          sourceUrl: selectedUrl,
        };
      }
    }
    return effective;
  }

  /**
   * Lazily verify a ref's differing server value. The default branch calls
   * this eagerly; other refs are checked on first resolveRef() use.
   */
  private ensureRefVerification(refName: string): Promise<void> {
    const stateEvent = this.stateManager.currentState;
    const stateRef = stateEvent?.refs.find((ref) => ref.name === refName);
    if (!stateRef) return Promise.resolve();

    const groups = this.groupServerRefCandidates(refName);
    const stateAnchor = this.getStateRefAnchor(stateRef.commitId, groups);
    const differing = groups.filter(
      (group) => !commitsMatch(group.commitId, stateAnchor),
    );
    const unsettled = differing.filter((group) => {
      const verification = this.ancestryVerifications.get(
        this.verificationKey(refName, stateAnchor, group.commitId),
      );
      return !verification || verification.status !== "complete";
    });
    if (unsettled.length === 0) return Promise.resolve();

    return Promise.all(
      unsettled.map((group) =>
        this.ensureCandidateVerification(
          refName,
          stateAnchor,
          this.pickGroupSource(group),
        ),
      ),
    ).then(() => {
      // Re-derive only after every candidate in this batch has settled. This
      // prevents a fast mirror from temporarily winning before a slower
      // mirror proves that it contains an incomparable fork.
      if (!this.isDisposed) this.setState((prev) => ({ ...prev }), false);
    });
  }

  private ensureCandidateVerification(
    refName: string,
    stateCommit: string,
    candidate: ServerRefCandidate,
  ): Promise<void> {
    const key = this.verificationKey(refName, stateCommit, candidate.commitId);
    const existing = this.ancestryVerifications.get(key);
    if (existing && existing.status !== "unavailable") {
      return existing.promise;
    }

    const verification: RefAncestryVerification = {
      stateCommit,
      gitCommit: candidate.commitId,
      status: "pending",
      descendsFromState: undefined as boolean | undefined,
      history: undefined,
      promise: Promise.resolve(),
    };
    const abort = new AbortController();
    verification.promise = this.getCommitHistory(
      candidate.commitId,
      ANCESTRY_VERIFICATION_MAX_DEPTH,
      abort.signal,
      [candidate.sourceUrl],
      stateCommit,
    )
      .then((history) => {
        if (
          this.isDisposed ||
          this.ancestryVerifications.get(key) !== verification
        ) {
          return;
        }
        if (!history || history.length === 0) {
          verification.status = "unavailable";
          return;
        }
        verification.history = history.map((commit) => commit.hash);
        verification.descendsFromState = history.some((commit) =>
          commitsMatch(commit.hash, stateCommit),
        );
        verification.status = "complete";
      })
      .catch(() => {
        // Transport/parser failures are not ancestry evidence. Keep a
        // distinct unavailable result so ref derivation can distinguish it
        // from a newly discovered candidate whose check has not started.
        if (this.ancestryVerifications.get(key) === verification) {
          verification.status = "unavailable";
        }
      });
    this.ancestryVerifications.set(key, verification);
    return verification.promise;
  }

  /** Resolve protocol truth for a ref, lazily ancestry-verifying if needed. */
  async resolveRef(refName: string): Promise<AuthoritativeRef | null> {
    await this.ensureRefVerification(refName);
    return this.state$.getValue().authoritativeRefs[refName] ?? null;
  }

  /** Change the shared display preference without affecting protocol truth. */
  setViewSource(source: ViewSource | "default"): void {
    const viewSource = source === "default" ? "authoritative" : source;
    if (this.state$.getValue().viewSource === viewSource) return;
    this.setState((prev) => ({ ...prev, viewSource }));
  }

  // -----------------------------------------------------------------------
  // Dynamic URL management
  // -----------------------------------------------------------------------

  /**
   * Add new clone URLs to the pool. Called when new announcement events
   * arrive and the clone URL list grows.
   *
   * New URLs are immediately tested (infoRefs fetch) if the pool has
   * already completed its initial fetch. If the pool is still in its
   * initial fetch, the new URLs are included in the current race.
   */
  addUrls(urls: string[]): void {
    const newUrls = this.urlManager.addUrls(urls);
    if (newUrls.length === 0) return;

    // Update state to include new URLs
    this.setState((prev) => ({
      ...prev,
      urls: this.urlManager.toStateRecord(),
    }));

    // If we've already fetched, probe the new URLs
    if (this.fetchedOnce && !this.fetching) {
      this.probeNewUrls(newUrls);
    }
    // If currently fetching, the new URLs will be picked up by the
    // in-progress fetch since urlManager is shared
  }

  /**
   * Probe newly-added URLs by fetching their infoRefs.
   * If any new URL is faster or has data the current winner doesn't,
   * it may become the new winner.
   */
  private async probeNewUrls(urls: string[]): Promise<void> {
    const abort = new AbortController();
    const signal = abort.signal;

    for (const url of urls) {
      // Short-circuit for non-HTTP URLs
      if (isNonHttpUrl(url)) {
        const tracker = this.urlManager.get(url);
        if (tracker) {
          tracker.recordPermanentFailure(
            `URL uses a non-HTTP scheme and cannot be fetched by the browser: ${url}`,
            "not-http",
          );
        }
        this.setState((prev) => ({
          ...prev,
          urls: this.urlManager.toStateRecord(),
          health: this.computeHealth(),
        }));
        continue;
      }

      try {
        const start = Date.now();
        const info = await this.http.fetchInfoRefs(url, signal);
        const latency = Date.now() - start;

        if (signal.aborted) return;

        const tracker = this.urlManager.get(url);
        if (tracker) {
          tracker.recordInfoRefsSuccess(info, latency);
        }

        // Re-evaluate winner
        const newWinner = this.urlManager.selectBestUrl(
          this.winnerUrl ?? undefined,
        );
        if (newWinner && newWinner !== this.winnerUrl) {
          this.winnerUrl = newWinner;
        }

        this.setState((prev) => ({
          ...prev,
          urls: this.urlManager.toStateRecord(),
          winnerUrl: this.winnerUrl,
          health: this.computeHealth(),
        }));
      } catch (err) {
        if (signal.aborted) return;
        const tracker = this.urlManager.get(url);
        if (tracker) {
          const { errorClass, kind } = classifyFetchError(err);
          const msg = err instanceof Error ? err.message : String(err);
          if (errorClass === "permanent") {
            tracker.recordPermanentFailure(msg, kind);
          } else {
            tracker.recordTransientError(msg, kind);
          }
        }
        this.setState((prev) => ({
          ...prev,
          urls: this.urlManager.toStateRecord(),
          health: this.computeHealth(),
        }));
      }
    }
  }

  // -----------------------------------------------------------------------
  // Nostr state event integration
  // -----------------------------------------------------------------------

  private onStateEventChange(stateEvent: StateEventInput): void {
    const prevState = this.stateManager.currentState;
    this.stateManager.update(stateEvent);

    if (stateEvent === undefined || stateEvent === null) {
      // State cleared or still loading — recompute warning
      this.setState((prev) => ({
        ...prev,
        warning: null,
      }));
      return;
    }

    // Re-emit so authoritativeHead reflects the new state head immediately
    // (the fetch paths below may not produce another emission, e.g. when the
    // refs already match what the servers reported).
    this.setState((prev) => ({ ...prev }));

    // If the state event changed and we've already fetched, check if we
    // need to re-fetch
    if (this.fetchedOnce && !this.fetching) {
      const refsChanged = !this.stateManager.refsMatchLastFetched(
        this.buildLastFetchedRefs(),
      );
      if (refsChanged) {
        // Invalidate infoRefs caches so we get fresh data
        for (const url of this.urlManager.getLiveUrls()) {
          this.cache.invalidateInfoRefs(url);
        }
        this.stateManager.scheduleBackoffFetch(() => this.startFetch());
        this.setState((prev) => ({
          ...prev,
          retryAt: this.stateManager.retryAt,
        }));
      }
    } else if (!this.fetchedOnce && !this.fetching) {
      // Haven't started yet — the next subscribe() will trigger startFetch
      // which will use the state event data
    } else if (this.fetching) {
      // Fetch in progress — if we just learned the state commit for the
      // first time, restart the fetch so the fast-path cache check runs
      if (prevState === undefined || prevState === null) {
        this.startFetch();
      }
    }
  }

  private buildLastFetchedRefs(): Record<string, string> {
    const refs: Record<string, string> = {};
    for (const tracker of this.urlManager.getAll()) {
      if (tracker.state.infoRefs) {
        for (const [refName, commitId] of Object.entries(
          tracker.state.infoRefs.refs,
        )) {
          if (!(refName in refs)) {
            refs[refName] = commitId;
          }
        }
      }
    }
    return refs;
  }

  // -----------------------------------------------------------------------
  // Fetch lifecycle
  // -----------------------------------------------------------------------

  private startFetch(): void {
    this.stateManager.cancelBackoff();
    // Clear retryAt in state since we're fetching now
    this.setState((prev) => ({ ...prev, retryAt: null }));
    this.runFetch();
  }

  /**
   * Discard advertised-ref snapshots and immediately re-check the git servers.
   *
   * A successful push changes refs outside the normal cache TTL. Refreshing at
   * that mutation boundary prevents a newly published Nostr state from being
   * compared against the pre-push advertisement while navigation moves to the
   * Code view.
   */
  private refreshAdvertisedRefs(): void {
    for (const url of this.urlManager.getLiveUrls()) {
      this.cache.invalidateInfoRefs(url);
    }
    this.startFetch();
  }

  private async runFetch(): Promise<void> {
    const allUrls = this.urlManager.getLiveUrls();
    if (allUrls.length === 0) {
      this.fetching = false;
      this.fetchedOnce = true;
      this.setState((prev) => ({
        ...prev,
        loading: false,
        pulling: false,
        error: "Could not reach any clone URL",
        health: "all-failed",
      }));
      return;
    }

    // Abort any previous in-flight fetch
    this.abort?.abort();
    const abort = new AbortController();
    this.abort = abort;
    const signal = abort.signal;

    this.fetching = true;

    const knownState = this.stateManager.currentState;
    const knownHeadCommit =
      knownState && knownState !== null ? knownState.headCommitId : undefined;
    const stateCreatedAt =
      knownState && knownState !== null ? knownState.createdAt : undefined;

    // -------------------------------------------------------------------
    // Fast-path: stale-while-revalidate from cache
    // -------------------------------------------------------------------
    let hasCachedData = false;

    if (knownHeadCommit) {
      // Check if any cached infoRefs shows git is ahead
      let cachedGitAheadCommit: string | null = null;
      let cachedGitAheadUrl: string | null = null;

      for (const url of allUrls) {
        const cachedInfo =
          this.cache.peekInfoRefs(url) ?? (await this.cache.getInfoRefs(url));
        if (signal.aborted) {
          this.fetching = false;
          return;
        }
        if (!cachedInfo) continue;
        const headRef = cachedInfo.symrefs["HEAD"];
        // Only compare against the HEAD commit when the server advertises a
        // HEAD symref. A server missing HEAD (e.g. a partial mirror) should
        // not drive the "git is ahead" fast-path.
        const headCommit = headRef ? cachedInfo.refs[headRef] : undefined;
        if (!headCommit) continue;
        const matchesState =
          headCommit.startsWith(knownHeadCommit) ||
          knownHeadCommit.startsWith(headCommit);
        if (!matchesState) {
          cachedGitAheadCommit = headCommit;
          cachedGitAheadUrl = url;
          break;
        }
      }

      if (
        cachedGitAheadCommit &&
        cachedGitAheadUrl &&
        stateCreatedAt !== undefined
      ) {
        // Git is ahead — check if we have the commit cached too
        const gitCommit =
          this.cache.peekCommit(cachedGitAheadCommit) ??
          (await this.cache.getCommit(cachedGitAheadCommit));
        if (signal.aborted) {
          this.fetching = false;
          return;
        }
        if (gitCommit) {
          const gitCommitterDate =
            gitCommit.committer?.timestamp ?? gitCommit.author.timestamp;
          this.setState((prev) => ({
            ...prev,
            loading: false,
            pulling: false,
            latestCommit: gitCommit,
            readmeContent:
              this.cache.getText(cachedGitAheadCommit!, "README.md") ?? null,
            readmeFilename:
              this.cache.getText(cachedGitAheadCommit!, "README.md") !==
              undefined
                ? "README.md"
                : null,
            warning: {
              kind: "state-behind-git",
              stateCommitId: knownHeadCommit,
              gitCommitId: gitCommit.hash,
              gitServerUrl: cachedGitAheadUrl!,
              stateCreatedAt,
              gitCommitterDate,
            },
          }));
          hasCachedData = true;
        }
      } else if (!cachedGitAheadCommit) {
        // No git-ahead situation — try to show the state commit from cache
        const cachedCommit =
          this.cache.peekCommit(knownHeadCommit) ??
          (await this.cache.getCommit(knownHeadCommit));
        if (signal.aborted) {
          this.fetching = false;
          return;
        }
        if (cachedCommit) {
          const cachedText = this.cache.getText(knownHeadCommit, "README.md");
          this.setState((prev) => ({
            ...prev,
            loading: true,
            pulling: true,
            latestCommit: cachedCommit,
            readmeContent: cachedText ?? prev.readmeContent,
            readmeFilename: cachedText ? "README.md" : prev.readmeFilename,
            warning: null,
          }));
          hasCachedData = true;
        }
      }
    }

    if (!hasCachedData) {
      this.setState((prev) => ({
        ...prev,
        loading: true,
        pulling: false,
        error: null,
        latestCommit: null,
        readmeContent: null,
        readmeFilename: null,
        defaultBranch: null,
        warning: null,
      }));
    }

    if (signal.aborted) {
      this.fetching = false;
      return;
    }

    // -------------------------------------------------------------------
    // Phase 1 shortcut: if we know the state HEAD, start fetching it now
    // -------------------------------------------------------------------
    let stateCommitFetched = false;
    let stateCommitFetchPending = !!knownHeadCommit;

    if (knownHeadCommit) {
      this.fetchStateCommit(knownHeadCommit, allUrls, signal).then(
        (result) => {
          if (signal.aborted) return;
          stateCommitFetchPending = false;
          if (result) {
            stateCommitFetched = true;
            this.maybeUpdateDisplay(
              result.commit,
              result.readmeContent,
              result.readmeFilename,
              true,
              knownHeadCommit,
              stateCreatedAt,
              signal,
            );
          }
        },
        () => {
          stateCommitFetchPending = false;
        },
      );
    }

    // -------------------------------------------------------------------
    // Phase 2: race infoRefs across all URLs
    // -------------------------------------------------------------------
    let infoRefsSettled = 0;
    const totalUrls = allUrls.length;
    let anyGitHeadDiffersFromState = false;
    let bestGitResult: { commit: Commit; url: string } | null = null;
    let defaultBranch: string | null = null;

    const infoRefsPromises = allUrls.map(async (url) => {
      const tracker = this.urlManager.getOrCreate(url);

      // Short-circuit immediately for non-HTTP URLs (SSH, git://, etc.)
      // — the browser cannot fetch these at all.
      if (isNonHttpUrl(url)) {
        tracker.recordPermanentFailure(
          `URL uses a non-HTTP scheme and cannot be fetched by the browser: ${url}`,
          "not-http",
        );
        this.setState((prev) => ({
          ...prev,
          urls: this.urlManager.toStateRecord(),
        }));
        return;
      }

      try {
        const start = Date.now();
        const info = await this.http.fetchInfoRefs(url, signal);
        const latency = Date.now() - start;

        if (signal.aborted) return;

        tracker.recordInfoRefsSuccess(info, latency);

        const headRef = info.symrefs["HEAD"];
        // Only use the HEAD commit for "ahead of Nostr" comparison when the
        // server advertises a HEAD symref. A server missing HEAD (e.g. a
        // mirror that only has non-default branches) cannot be used as an
        // authority for what the default branch points to — using its first
        // arbitrary ref would produce false "git is ahead" warnings.
        const headCommit = headRef ? info.refs[headRef] : undefined;

        // Extract default branch
        if (headRef?.startsWith("refs/heads/") && !defaultBranch) {
          defaultBranch = headRef.replace("refs/heads/", "");
          this.setState((prev) => ({ ...prev, defaultBranch }));
        }

        // Update state with this URL's result
        this.setState((prev) => ({
          ...prev,
          urls: this.urlManager.toStateRecord(),
        }));

        if (!headCommit) return;

        const matchesStateCommit =
          knownHeadCommit &&
          (headCommit.startsWith(knownHeadCommit) ||
            knownHeadCommit.startsWith(headCommit));

        if (!matchesStateCommit) {
          anyGitHeadDiffersFromState = true;
          // Fetch this commit to compare with state
          const result = await this.http.fetchCommit(
            url,
            headCommit,
            info.capabilities.includes("filter"),
            signal,
          );
          if (signal.aborted || !result) return;

          const committerDate =
            result.commit.committer?.timestamp ??
            result.commit.author.timestamp;
          if (
            !bestGitResult ||
            committerDate >
              (bestGitResult.commit.committer?.timestamp ??
                bestGitResult.commit.author.timestamp)
          ) {
            bestGitResult = { commit: result.commit, url };
          }

          this.maybeUpdateDisplay(
            result.commit,
            result.readmeContent,
            result.readmeFilename,
            false,
            knownHeadCommit,
            stateCreatedAt,
            signal,
          );
        } else if (!knownHeadCommit) {
          // No state event — fetch the git server's HEAD
          const result = await this.http.fetchCommit(
            url,
            headCommit,
            info.capabilities.includes("filter"),
            signal,
          );
          if (signal.aborted || !result) return;
          this.maybeUpdateDisplay(
            result.commit,
            result.readmeContent,
            result.readmeFilename,
            false,
            knownHeadCommit,
            stateCreatedAt,
            signal,
          );
        }
      } catch (err) {
        if (signal.aborted) return;
        const { errorClass, kind } = classifyFetchError(err);
        const msg = err instanceof Error ? err.message : String(err);
        if (errorClass === "permanent") {
          tracker.recordPermanentFailure(msg, kind);
        } else {
          tracker.recordTransientError(msg, kind);
        }
        this.setState((prev) => ({
          ...prev,
          urls: this.urlManager.toStateRecord(),
        }));
      } finally {
        if (!signal.aborted) {
          infoRefsSettled++;
          if (infoRefsSettled === totalUrls) {
            this.onAllInfoRefsSettled(
              signal,
              knownHeadCommit,
              stateCreatedAt,
              stateCommitFetched,
              stateCommitFetchPending,
              anyGitHeadDiffersFromState,
              bestGitResult,
            );
          }
        }
      }
    });

    // Don't await — the promises settle independently and update state
    // as they go. The pool is ready to serve cached data immediately.
    void Promise.allSettled(infoRefsPromises);
  }

  /**
   * Fetch the state commit from any URL that's likely to have it.
   */
  private async fetchStateCommit(
    commitHash: string,
    urls: string[],
    signal: AbortSignal,
  ): Promise<{
    commit: Commit;
    readmeContent: string | null;
    readmeFilename: string | null;
  } | null> {
    // Filter to URLs whose cached infoRefs match the state commit
    const likelyUrls = urls.filter((url) => {
      const cached = this.cache.peekInfoRefs(url);
      if (!cached) return true;
      const headRef = cached.symrefs["HEAD"];
      const headCommit = headRef
        ? cached.refs[headRef]
        : Object.values(cached.refs)[0];
      if (!headCommit) return true;
      return (
        headCommit.startsWith(commitHash) || commitHash.startsWith(headCommit)
      );
    });

    if (likelyUrls.length === 0) return null;

    try {
      return await Promise.any(
        likelyUrls.map(async (url) => {
          const result = await this.http.fetchCommit(
            url,
            commitHash,
            true,
            signal,
          );
          if (!result) throw new Error("not found");
          return result;
        }),
      );
    } catch {
      return null;
    }
  }

  /**
   * Update the displayed commit if the new result is newer.
   */
  private maybeUpdateDisplay(
    commit: Commit,
    readmeContent: string | null,
    readmeFilename: string | null,
    isStateCommit: boolean,
    knownHeadCommit: string | undefined,
    stateCreatedAt: number | undefined,
    signal: AbortSignal,
  ): void {
    if (signal.aborted) return;

    const committerDate =
      commit.committer?.timestamp ?? commit.author.timestamp;
    const current = this.state$.getValue();

    const currentCommitterDate = current.latestCommit
      ? (current.latestCommit.committer?.timestamp ??
        current.latestCommit.author.timestamp)
      : 0;

    if (!current.latestCommit || committerDate > currentCommitterDate) {
      this.setState((prev) => ({
        ...prev,
        loading: false,
        pulling: false,
        latestCommit: commit,
        readmeContent: readmeContent ?? prev.readmeContent,
        readmeFilename: readmeFilename ?? prev.readmeFilename,
      }));
    }
  }

  /**
   * Called when all infoRefs fetches have settled.
   */
  private onAllInfoRefsSettled(
    signal: AbortSignal,
    knownHeadCommit: string | undefined,
    stateCreatedAt: number | undefined,
    stateCommitFetched: boolean,
    stateCommitFetchPending: boolean,
    anyGitHeadDiffersFromState: boolean,
    bestGitResult: { commit: Commit; url: string } | null,
  ): void {
    if (signal.aborted) return;

    // Select winner
    this.winnerUrl = this.urlManager.selectBestUrl(this.winnerUrl ?? undefined);

    const current = this.state$.getValue();

    // Compute warning
    let warning: PoolWarning | null = null;

    if (
      knownHeadCommit &&
      !stateCommitFetched &&
      anyGitHeadDiffersFromState &&
      !stateCommitFetchPending
    ) {
      warning = {
        kind: "state-commit-unavailable",
        stateCommitId: knownHeadCommit,
      };
    } else if (
      knownHeadCommit &&
      stateCreatedAt !== undefined &&
      bestGitResult &&
      anyGitHeadDiffersFromState
    ) {
      const displayHash = current.latestCommit?.hash;
      if (
        displayHash &&
        !displayHash.startsWith(knownHeadCommit) &&
        !knownHeadCommit.startsWith(displayHash)
      ) {
        const gitCommitterDate =
          bestGitResult.commit.committer?.timestamp ??
          bestGitResult.commit.author.timestamp;
        warning = {
          kind: "state-behind-git",
          stateCommitId: knownHeadCommit,
          gitCommitId: bestGitResult.commit.hash,
          gitServerUrl: bestGitResult.url,
          stateCreatedAt,
          gitCommitterDate,
        };
      }
    }

    this.fetching = false;
    this.fetchedOnce = true;

    const hasResult = current.latestCommit !== null;
    const stateEventForRefs = this.stateManager.currentState;
    const stateEose = stateEventForRefs !== undefined;

    // Compute per-URL ref statuses and cross-ref discrepancies
    const { urlRefStatuses, urlRefCommits, crossRefDiscrepancies } =
      computeRefStatuses(
        this.urlManager.getAll(),
        stateEventForRefs,
        stateEose,
      );

    // Push computed ref statuses into each tracker
    for (const tracker of this.urlManager.getAll()) {
      const refStatus = urlRefStatuses.get(tracker.url) ?? {};
      const refCommits = urlRefCommits.get(tracker.url) ?? {};
      tracker.updateRefStatus(refStatus, refCommits);
    }

    this.setState((prev) => ({
      ...prev,
      loading: false,
      pulling: false,
      error: hasResult ? null : "Could not reach any clone URL",
      health: this.computeHealth(),
      winnerUrl: this.winnerUrl,
      warning,
      lastCheckedAt: hasResult
        ? Math.floor(Date.now() / 1000)
        : prev.lastCheckedAt,
      urls: this.urlManager.toStateRecord(),
      crossRefDiscrepancies,
      retryAt: this.stateManager.retryAt,
    }));

    // Handle backoff for state event mismatch
    if (hasResult) {
      const lastFetchedRefs = this.buildLastFetchedRefs();
      const stateEvent = this.stateManager.currentState;
      if (
        stateEvent &&
        stateEvent !== null &&
        !this.stateManager.refsMatchLastFetched(lastFetchedRefs)
      ) {
        // Check if git is ahead (server has different commit than state).
        // Only consider servers that advertise a HEAD symref — servers
        // missing HEAD cannot be used as an authority for the default branch.
        const gitIsAhead = this.urlManager.getAll().some((t) => {
          if (t.status !== "ok" || !t.state.infoRefs) return false;
          const headRef = t.state.infoRefs.symrefs["HEAD"];
          if (!headRef) return false;
          const headCommit = t.state.infoRefs.refs[headRef];
          if (!headCommit) return false;
          return (
            !headCommit.startsWith(stateEvent.headCommitId) &&
            !stateEvent.headCommitId.startsWith(headCommit)
          );
        });

        if (!gitIsAhead) {
          // State is ahead of git — servers haven't caught up yet, retry
          this.stateManager.scheduleBackoffFetch(() =>
            this.refreshAdvertisedRefs(),
          );
          // Update retryAt in state now that it's been set
          this.setState((prev) => ({
            ...prev,
            retryAt: this.stateManager.retryAt,
          }));
        } else {
          // Git is ahead — reset backoff
          this.stateManager.resetBackoff();
        }
      } else {
        this.stateManager.resetBackoff();
      }
    } else {
      // All failed — retry if there are retryable URLs
      const hasRetryable = this.urlManager.getLiveUrls().length > 0;
      if (hasRetryable && this.stateManager.currentState) {
        this.stateManager.scheduleBackoffFetch(() =>
          this.refreshAdvertisedRefs(),
        );
      }
    }
  }

  private computeHealth(): PoolHealth {
    const all = this.urlManager.getAll();
    if (all.length === 0) return "idle";

    const ok = all.filter((t) => t.status === "ok").length;
    const failed = all.filter((t) => t.status === "permanent-failure").length;
    const untested = all.filter((t) => t.status === "untested").length;

    if (ok === 0 && failed === all.length) return "all-failed";
    if (untested === all.length) return "connecting";
    if (ok > 0 && failed === 0) return "ok";
    if (ok > 0) return "degraded";
    return "connecting";
  }

  // -----------------------------------------------------------------------
  // Public API — git operations
  // -----------------------------------------------------------------------

  /**
   * Get the directory tree at a commit hash.
   * Routes through the winning URL with fallback.
   *
   * @param fallbackUrls - Extra URLs to try after the pool's own URLs if the
   *   data is not found. These are not tracked by the pool and are only used
   *   for this single operation (e.g. PR author's fork clone URLs).
   */
  async getTree(
    commitHash: string,
    nestLimit: number,
    signal: AbortSignal,
    fallbackUrls?: string[],
  ): Promise<Tree | null> {
    // Check cache first (synchronous peek)
    const cached = this.cache.peekTree(commitHash, nestLimit);
    if (cached) return cached;

    // Try async cache (IDB)
    const idbCached = await this.cache.getTree(commitHash, nestLimit);
    if (idbCached) return idbCached;

    // Fetch from git server
    return this.withFallback(
      signal,
      async (url) => {
        const start = Date.now();
        const isPoolUrl = this.urlManager.get(url) !== undefined;
        try {
          const result = await this.http.fetchTree(
            url,
            commitHash,
            nestLimit,
            signal,
          );
          const tracker = this.urlManager.get(url);
          if (result) {
            tracker?.recordOperationSuccess(Date.now() - start);
            tracker?.recordObjectFetch(commitHash, "ok");
          } else {
            // fetchTree returned null without throwing — the server's response
            // genuinely lacked the objects for this commit.
            tracker?.recordObjectFetch(commitHash, "object-missing");
          }
          if (!result && isPoolUrl) {
            this.setState((prev) => ({
              ...prev,
              urls: this.urlManager.toStateRecord(),
            }));
          }
          return result;
        } catch (err) {
          if (signal.aborted) throw err;
          const classified = classifyObjectFetchError(err);
          const tracker = this.urlManager.get(url);
          if (classified.missing) {
            // The server returned a valid response (or "not our ref") without
            // the commit's objects — genuine missing objects.
            tracker?.recordObjectFetch(commitHash, "object-missing");
          } else {
            // The git-upload-pack call or packfile transport/parse failed.
            // This is NOT evidence the server lacks the objects. Record it as
            // a fetch error against the object-fetch outcome WITHOUT flipping
            // the infoRefs-based connection status (the server may still be
            // perfectly reachable for infoRefs).
            tracker?.recordObjectFetch(
              commitHash,
              "fetch-error",
              classified.kind,
            );
          }
          if (isPoolUrl) {
            this.setState((prev) => ({
              ...prev,
              urls: this.urlManager.toStateRecord(),
            }));
          }
          // Move on to the next URL. We've already recorded the per-URL
          // outcome ourselves, so return null instead of rethrowing to avoid
          // withFallback recording a connection-level failure for what may be
          // a transport-only object-fetch error.
          return null;
        }
      },
      fallbackUrls,
    );
  }

  /**
   * Get the full recursive tree for a commit using blob:none (lightweight —
   * only tree metadata, no file content). Also returns the parsed commit.
   *
   * This is the same fetch strategy used by getCommitRange for diffing.
   * The commit object is cached as a side-effect, so subsequent
   * getSingleCommit calls for the same hash are instant.
   *
   * @param fallbackUrls - Extra URLs to try after the pool's own URLs if the
   *   data is not found. Not tracked by the pool.
   */
  async getFullTree(
    commitHash: string,
    signal: AbortSignal,
    fallbackUrls?: string[],
  ): Promise<{ commit: Commit; tree: Tree } | null> {
    return this.withFallback(
      signal,
      async (url) => {
        const start = Date.now();
        const result = await this.http.fetchFullTree(url, commitHash, signal);
        if (result) {
          const tracker = this.urlManager.get(url);
          tracker?.recordOperationSuccess(Date.now() - start);
        }
        return result;
      },
      fallbackUrls,
    );
  }

  /**
   * Get a blob by its object hash.
   *
   * @param fallbackUrls - Extra URLs to try after the pool's own URLs if the
   *   data is not found. Not tracked by the pool.
   */
  async getBlob(
    blobHash: string,
    signal: AbortSignal,
    fallbackUrls?: string[],
  ): Promise<Uint8Array | null> {
    const cached = this.cache.peekBlob(blobHash);
    if (cached) return cached;

    const idbCached = await this.cache.getBlob(blobHash);
    if (idbCached) return idbCached;

    return this.withFallback(
      signal,
      async (url) => {
        const start = Date.now();
        const result = await this.http.fetchBlob(url, blobHash, signal);
        if (result) {
          const tracker = this.urlManager.get(url);
          tracker?.recordOperationSuccess(Date.now() - start);
        }
        return result;
      },
      fallbackUrls,
    );
  }

  /**
   * Get several blobs, using one upload-pack request for uncached hashes.
   *
   * If no single mirror can serve the complete batch, missing hashes retain
   * the established per-object mirror and fallback-URL behavior.
   */
  async getBlobs(
    blobHashes: string[],
    signal: AbortSignal,
    fallbackUrls?: string[],
  ): Promise<Map<string, Uint8Array>> {
    const blobs = new Map<string, Uint8Array>();
    const uniqueHashes = [...new Set(blobHashes)];

    for (const hash of uniqueHashes) {
      const cached = this.cache.peekBlob(hash);
      if (cached) blobs.set(hash, cached);
    }

    const l2Hashes = uniqueHashes.filter((hash) => !blobs.has(hash));
    const l2Results = await Promise.all(
      l2Hashes.map(
        async (hash) => [hash, await this.cache.getBlob(hash)] as const,
      ),
    );
    for (const [hash, data] of l2Results) {
      if (data) blobs.set(hash, data);
    }

    if (signal.aborted) return blobs;
    const missing = uniqueHashes.filter((hash) => !blobs.has(hash));
    if (missing.length === 0) return blobs;

    const batch = await this.withFallback(
      signal,
      async (url) => {
        const start = Date.now();
        const result = await this.http.fetchBlobs(url, missing, signal);
        if (result && result.size > 0) {
          const tracker = this.urlManager.get(url);
          tracker?.recordOperationSuccess(Date.now() - start);
        }
        return result;
      },
      fallbackUrls,
    );
    if (batch) {
      for (const [hash, data] of batch) blobs.set(hash, data);
    }

    if (signal.aborted) return blobs;
    const stillMissing = uniqueHashes.filter((hash) => !blobs.has(hash));
    const fallbackResults = await Promise.all(
      stillMissing.map(
        async (hash) =>
          [hash, await this.getBlob(hash, signal, fallbackUrls)] as const,
      ),
    );
    for (const [hash, data] of fallbackResults) {
      if (data) blobs.set(hash, data);
    }

    return blobs;
  }

  /**
   * Get an object by path within a commit.
   *
   * @param fallbackUrls - Extra URLs to try after the pool's own URLs if the
   *   data is not found. Not tracked by the pool.
   */
  async getObjectByPath(
    commitHash: string,
    path: string,
    signal: AbortSignal,
    fallbackUrls?: string[],
  ): Promise<{ hash: string; isDir: boolean; data: Uint8Array | null } | null> {
    return this.withFallback(
      signal,
      async (url) => {
        const start = Date.now();
        const result = await this.http.fetchObjectByPath(
          url,
          commitHash,
          path,
          signal,
        );
        if (result) {
          const tracker = this.urlManager.get(url);
          tracker?.recordOperationSuccess(Date.now() - start);
          return {
            hash: result.entry.hash,
            isDir: result.entry.isDir,
            data: result.data,
          };
        }
        return null;
      },
      fallbackUrls,
    );
  }

  /**
   * Get commit history for a commit hash.
   *
   * @param fallbackUrls - Extra URLs to try after the pool's own URLs if the
   *   data is not found. Not tracked by the pool.
   */
  async getCommitHistory(
    commitHash: string,
    maxCommits: number,
    signal: AbortSignal,
    fallbackUrls?: string[],
    untilHash?: string,
  ): Promise<Commit[] | null> {
    const cached = this.cache.peekCommitHistory(commitHash, maxCommits);
    if (cached) return cached;

    const idbCached = await this.cache.getCommitHistory(commitHash, maxCommits);
    if (idbCached) return idbCached;

    return this.withFallback(
      signal,
      async (url) => {
        const start = Date.now();
        const result = await this.http.fetchCommitHistory(
          url,
          commitHash,
          maxCommits,
          signal,
          untilHash,
        );
        if (result) {
          const tracker = this.urlManager.get(url);
          tracker?.recordOperationSuccess(Date.now() - start);
        }
        return result;
      },
      fallbackUrls,
    );
  }

  /**
   * Fetch full, packable git objects for a commit range headed by `tipCommitId`.
   *
   * This is used when pushing browser-created PR merge commits. The merge commit
   * has the PR tip as its second parent, so the target server must also receive
   * any PR branch commits/blobs it does not already have. `stopAtCommitId` is
   * used only to estimate the required shallow depth; the returned pack may
   * include extra already-known objects, which git-receive-pack accepts.
   */
  async getPackableObjectsForCommitRange(
    tipCommitId: string,
    stopAtCommitId: string,
    signal: AbortSignal,
    fallbackUrls?: string[],
    maxDepth = 200,
  ): Promise<PackableObject[] | null> {
    const history = await this.getCommitHistory(
      tipCommitId,
      maxDepth,
      signal,
      fallbackUrls,
      stopAtCommitId,
    );
    if (signal.aborted) return null;

    if (!history || history.length === 0) return null;

    const requestedDepth = estimateDeepenDepth(
      history,
      tipCommitId,
      stopAtCommitId,
    );

    return this.withFallback(
      signal,
      async (url) => {
        const start = Date.now();
        const haveCommitIds =
          stopAtCommitId && stopAtCommitId !== ZERO_HASH
            ? [stopAtCommitId]
            : [];
        const result = await this.http.fetchPackableObjects(
          url,
          tipCommitId,
          requestedDepth,
          signal,
          haveCommitIds,
        );
        if (result) {
          const tracker = this.urlManager.get(url);
          tracker?.recordOperationSuccess(Date.now() - start);
        }
        return result;
      },
      fallbackUrls,
    );
  }

  /** Fetch raw, packable objects for a specific advertised git object. */
  async getPackableObjectsForObject(
    objectHash: string,
    signal: AbortSignal,
    fallbackUrls?: string[],
  ): Promise<PackableObject[] | null> {
    return this.withFallback(
      signal,
      async (url) => {
        const start = Date.now();
        const result = await this.http.fetchPackableObjects(
          url,
          objectHash,
          1,
          signal,
        );
        if (result) {
          const tracker = this.urlManager.get(url);
          tracker?.recordOperationSuccess(Date.now() - start);
        }
        return result;
      },
      fallbackUrls,
    );
  }

  /**
   * Push one ref update (plus any state-event refs a server is missing) to
   * the given Grasp servers, in parallel, tolerating lagging mirrors.
   *
   * The pool supplies catch-up objects via
   * {@link getPackableObjectsForCommitRange}; see `grasp-push.ts` for the
   * per-server semantics. Guards the signed Nostr state with a fast-forward
   * check before anything is sent. Resolves once at least one server
   * accepted — the rest keep syncing in the background, reporting through
   * `options.onUpdate` and `summary.settled`. Throws when every server
   * rejected.
   *
   * @param objects - All objects required for the primary update.
   * @param refUpdate - The primary ref update (consensus old hash → new hash).
   * @param options.targetCloneUrls - Grasp server clone URLs to push to
   *   (usually the repo's announced Grasp URLs, not the pool's full URL set).
   * @param options.currentStateEvent - Current kind:30618 state; its refs form
   *   the post-push ref set every server is verified against.
   * @param options.fallbackUrls - Extra URLs for catch-up object fetches.
   * @param options.onUpdate - Called with a fresh summary snapshot every time
   *   a server settles, including background settles after resolution.
   */
  async pushRefUpdate(
    objects: PackableObject[],
    refUpdate: RefUpdate,
    options: {
      targetCloneUrls: string[];
      currentStateEvent?: NostrEvent | null;
      fallbackUrls?: string[];
      signal?: AbortSignal;
      onUpdate?: (summary: PushDeliverySummary) => void;
    },
  ): Promise<PushDeliverySummary> {
    const summary = await pushRefUpdateToGraspServers({
      cloneUrls: options.targetCloneUrls,
      objects,
      refUpdate,
      currentStateEvent: options.currentStateEvent,
      onUpdate: (snapshot) => {
        // Once the last background push settles, revalidate info/refs again
        // so late-syncing mirrors are reflected without waiting for a poll.
        if (snapshot.pendingCount === 0) this.refreshAdvertisedRefs();
        options.onUpdate?.(snapshot);
      },
      fetchCatchUpObjects: async (
        tipCommitId,
        stopAtCommitId,
        includeObjectIds,
      ) => {
        if (!tipCommitId || tipCommitId === ZERO_HASH) {
          return Promise.resolve(null);
        }
        const signal = options.signal ?? new AbortController().signal;
        const objects = await this.getPackableObjectsForCommitRange(
          tipCommitId,
          stopAtCommitId,
          signal,
          options.fallbackUrls,
        );
        if (!objects || !includeObjectIds || includeObjectIds.length === 0) {
          return objects;
        }

        const fetchedObjects = [...objects];
        for (const objectId of includeObjectIds) {
          if (fetchedObjects.some((object) => object.hash === objectId)) {
            continue;
          }

          const extraObjects = await this.getPackableObjectsForObject(
            objectId,
            signal,
            options.fallbackUrls,
          );
          if (!extraObjects?.some((object) => object.hash === objectId)) {
            return null;
          }
          fetchedObjects.push(...extraObjects);
        }

        return fetchedObjects;
      },
    });

    // The server-side refs have just changed, but info/refs is cached by the
    // pool. Revalidate immediately rather than waiting for the state-event
    // backoff poll (or a full page reload) to notice this merge. When pushes
    // are still settling in the background, the onUpdate wrapper above
    // refreshes again once the last one lands.
    if (summary.pendingCount > 0) this.refreshAdvertisedRefs();

    return summary;
  }

  /**
   * Get a single commit by hash.
   *
   * @param fallbackUrls - Extra URLs to try after the pool's own URLs if the
   *   data is not found. Not tracked by the pool.
   */
  async getSingleCommit(
    commitHash: string,
    signal: AbortSignal,
    fallbackUrls?: string[],
  ): Promise<Commit | null> {
    if (!/^[0-9a-f]{40}$/i.test(commitHash)) {
      throw new Error(
        `Invalid commit ID "${commitHash}": expected a 40-character hexadecimal SHA-1`,
      );
    }

    const cached = this.cache.peekCommit(commitHash);
    if (cached) return cached;

    const idbCached = await this.cache.getCommit(commitHash);
    if (idbCached) return idbCached;

    return this.withFallback(
      signal,
      async (url) => {
        const start = Date.now();
        const result = await this.http.fetchSingleCommit(
          url,
          commitHash,
          signal,
        );
        if (result) {
          const tracker = this.urlManager.get(url);
          tracker?.recordOperationSuccess(Date.now() - start);
        }
        return result;
      },
      fallbackUrls,
    );
  }

  /**
   * Prove that an advertised server can supply an exact commit now. Unlike
   * getSingleCommit, this deliberately bypasses both memory and IndexedDB.
   */
  async probeCommitOnNetwork(
    commitHash: string,
    signal: AbortSignal,
    fallbackUrls?: string[],
  ): Promise<Commit | null> {
    if (!/^[0-9a-f]{40}$/i.test(commitHash)) {
      throw new Error(
        `Invalid commit ID "${commitHash}": expected a 40-character hexadecimal SHA-1`,
      );
    }

    return this.withFallback(
      signal,
      async (url) => {
        const start = Date.now();
        const result = await this.http.fetchSingleCommitFromNetwork(
          url,
          commitHash,
          signal,
        );
        if (result) {
          const tracker = this.urlManager.get(url);
          tracker?.recordOperationSuccess(Date.now() - start);
        }
        return result;
      },
      fallbackUrls,
    );
  }

  /**
   * Find the merge base between a PR tip commit and the default branch.
   *
   * Strategy:
   *   1. Fetch the default branch's commit history (up to `maxDepth` commits)
   *      and build a Set of those hashes.
   *   2. Walk the PR tip's commit chain (up to `maxDepth` commits), checking
   *      each commit against the Set.
   *   3. The first PR-chain commit found in the default branch history is the
   *      merge base.
   *
   * Fallback (author heuristic):
   *   If no commit from the PR chain appears in the default branch history
   *   (e.g. the branch diverged further back than `maxDepth`), we fall back
   *   to scanning the PR commit chain for the first commit whose author email
   *   differs from the PR tip's author email. This catches the common case
   *   where all PR commits share the same author but the base commit was
   *   authored by someone else (e.g. a maintainer's merge commit).
   *
   * Returns the merge-base commit hash, or null if it cannot be determined.
   *
   * @param prTipCommitId   - The tip commit of the PR branch.
   * @param fallbackUrls    - Extra clone URLs to try (e.g. PR author's fork).
   * @param maxDepth        - How many commits to walk in each chain (default 200).
   */
  async findMergeBase(
    prTipCommitId: string,
    signal: AbortSignal,
    fallbackUrls?: string[],
    maxDepth = 200,
  ): Promise<string | null> {
    // Resolve the default branch commit from infoRefs.
    const info = this.getInfoRefs();
    if (!info) return null;

    const headRef = info.symrefs["HEAD"];
    const defaultBranchCommit = headRef
      ? info.refs[headRef]
      : Object.values(info.refs)[0];
    if (!defaultBranchCommit) return null;

    // If the PR tip IS the default branch head, there are no PR-specific commits.
    if (defaultBranchCommit === prTipCommitId) return null;

    // Fetch both commit chains in parallel.
    const [prChain, defaultChain] = await Promise.all([
      this.getCommitHistory(prTipCommitId, maxDepth, signal, fallbackUrls),
      this.getCommitHistory(defaultBranchCommit, maxDepth, signal),
    ]);

    if (signal.aborted) return null;

    if (!prChain || prChain.length === 0) return null;

    if (defaultChain && defaultChain.length > 0) {
      // Build a set of hashes from the default branch chain.
      const defaultSet = new Set(defaultChain.map((c) => c.hash));

      // Walk the PR chain from tip to root, return the first commit that
      // appears in the default branch history.
      for (const commit of prChain) {
        if (defaultSet.has(commit.hash)) {
          return commit.hash;
        }
      }
    }

    // Author heuristic fallback: find the first commit in the PR chain whose
    // author email differs from the PR tip's author email. This handles the
    // common case where the PR author's commits are at the top of the chain
    // and the base commit was authored by someone else.
    const prTipAuthorEmail = prChain[0]?.author.email;
    if (prTipAuthorEmail) {
      for (let i = 1; i < prChain.length; i++) {
        if (prChain[i].author.email !== prTipAuthorEmail) {
          // The previous commit (i-1) is the last PR-authored commit;
          // this commit (i) is the first base commit.
          return prChain[i].hash;
        }
      }
    }

    return null;
  }

  /**
   * Find the merge base (lowest common ancestor) between two explicit commits.
   *
   * Unlike {@link findMergeBase}, this does NOT consult `infoRefs` for the
   * "other" side — both endpoints are passed in. This matters for merging:
   * the merge tree MUST be computed against the real common ancestor of the
   * branch we are advancing (`commitA`, the current default-branch tip) and
   * the commit we are merging in (`commitB`, the PR/patch tip), regardless of
   * any `merge-base` tag the PR event claims (which may be wrong — that is
   * exactly the bug an incorrect ngit merge base introduced).
   *
   * Strategy: walk `commitB`'s ancestry newest-first and return the first
   * commit that also appears in `commitA`'s ancestry. For the linear +
   * single-merge histories these repos produce this is the correct merge base.
   *
   * Returns the merge-base commit hash, or null if none is found within
   * `maxDepth` commits on either side.
   *
   * @param commitA      - Current default-branch tip ("ours").
   * @param commitB      - The commit being merged in ("theirs", e.g. PR tip).
   * @param fallbackUrls - Extra clone URLs to try (e.g. PR author's fork).
   * @param maxDepth     - How many commits to walk in each chain (default 200).
   */
  async findMergeBaseBetween(
    commitA: string,
    commitB: string,
    signal: AbortSignal,
    fallbackUrls?: string[],
    maxDepth = 200,
  ): Promise<string | null> {
    if (commitA === commitB) return commitA;

    const [chainA, chainB] = await Promise.all([
      this.getCommitHistory(commitA, maxDepth, signal),
      this.getCommitHistory(commitB, maxDepth, signal, fallbackUrls),
    ]);

    if (signal.aborted) return null;
    if (!chainA || chainA.length === 0 || !chainB || chainB.length === 0) {
      return null;
    }

    return findMergeBaseInHistories(commitA, commitB, chainA, chainB);
  }

  /**
   * Compare two explicit commits without downloading their full capped
   * histories up front. The graph is expanded through bounded checkpoints and
   * stops as soon as both the merge base and complete exclusive ranges are
   * available.
   */
  async compareCommits(
    baseCommitId: string,
    headCommitId: string,
    signal: AbortSignal,
    fallbackUrls?: string[],
    maxDepth = 200,
  ): Promise<CommitComparisonData | null> {
    if (baseCommitId === headCommitId) {
      const commit = await this.getSingleCommit(
        baseCommitId,
        signal,
        fallbackUrls,
      );
      return commit
        ? {
            mergeBaseId: commit.hash,
            baseCommit: commit,
            headCommit: commit,
            baseOnlyCommits: [],
            headOnlyCommits: [],
          }
        : null;
    }

    for (const depth of comparisonGraphDepths(maxDepth)) {
      const [baseHistory, headHistory] = await Promise.all([
        this.getCommitHistory(baseCommitId, depth, signal),
        this.getCommitHistory(headCommitId, depth, signal, fallbackUrls),
      ]);

      if (signal.aborted) return null;
      if (
        !baseHistory ||
        baseHistory.length === 0 ||
        !headHistory ||
        headHistory.length === 0
      ) {
        return null;
      }

      const comparison = comparisonFromHistories(
        baseCommitId,
        headCommitId,
        baseHistory,
        headHistory,
      );
      if (comparison) return comparison;
    }

    return null;
  }

  /**
   * Count how many commits the default branch is ahead of `mergeBase`.
   *
   * Fast path: reuses the default-branch history already cached by
   * `findMergeBase` (keyed at `batchSize=200`) — typically a single
   * in-memory array lookup with no network traffic.
   *
   * Lazy-load path: if the merge base is not found in the first batch,
   * fetches additional 200-commit batches walking backwards from the tail
   * of the previous batch. Each batch is cached individually so subsequent
   * calls for the same or nearby merge bases are also fast.
   *
   * Returns 0 when `mergeBase` IS the current HEAD (fully up-to-date).
   * Returns null when the walk fails, the server is unreachable, or the
   * merge base is not found within `maxTotal` commits.
   *
   * @param batchSize - Commits per fetch (default 200, matches findMergeBase).
   * @param maxTotal  - Hard cap on total commits walked (default 5000).
   * @param tipCommitId - Explicit effective default-branch tip. When omitted,
   *   the pool resolves HEAD from its effective ref view.
   */
  async countCommitsBehind(
    mergeBase: string,
    signal: AbortSignal,
    batchSize = 200,
    maxTotal = 5000,
    tipCommitId?: string,
  ): Promise<number | null> {
    const info = tipCommitId ? null : this.getEffectiveInfoRefs();
    const headRef = info?.symrefs["HEAD"];
    const defaultBranchCommit =
      tipCommitId ??
      (headRef ? info?.refs[headRef] : info && Object.values(info.refs)[0]);
    if (!defaultBranchCommit) return null;

    if (defaultBranchCommit === mergeBase) return 0;

    let offset = 0; // total commits walked so far
    let batchStart = defaultBranchCommit;

    while (offset < maxTotal) {
      if (signal.aborted) return null;

      // getCommitHistory checks L1 → L2 → network, and caches the result.
      const batch = await this.getCommitHistory(batchStart, batchSize, signal);

      if (!batch || batch.length === 0 || signal.aborted) return null;

      const idx = batch.findIndex((c) => c.hash === mergeBase);
      if (idx !== -1) return offset + idx;

      offset += batch.length;

      // The last commit in the batch is the oldest one fetched. Walk to its
      // first parent to start the next batch without overlap.
      const tail = batch[batch.length - 1];
      if (tail.parents.length === 0) return null; // reached root

      batchStart = tail.parents[0];

      // If the tail itself is the merge base we would have caught it above;
      // if its parent is the merge base we'll find it in the next batch.
    }

    return null; // exceeded maxTotal
  }

  /**
   * Find the newest commit on the default branch whose committer timestamp
   * is ≤ `targetTimestamp`.
   *
   * This is used as a heuristic merge-base for NIP-34 patches that are
   * missing a `parent-commit` tag. The patch's author timestamp is used as
   * the target: the newest default-branch commit that existed at or before
   * the patch was authored is a reasonable approximation of the base.
   *
   * Strategy:
   *   Walk the default branch history in batches (newest-first). The first
   *   commit whose committer timestamp is ≤ targetTimestamp is returned.
   *
   * Returns the commit hash, or null if it cannot be determined.
   *
   * @param targetTimestamp - Unix timestamp (seconds) to search at or before.
   * @param signal          - AbortSignal for cancellation.
   * @param batchSize       - Commits per fetch (default 200).
   * @param maxTotal        - Hard cap on total commits walked (default 2000).
   */
  async findCommitBeforeTimestamp(
    targetTimestamp: number,
    signal: AbortSignal,
    batchSize = 200,
    maxTotal = 2000,
  ): Promise<string | null> {
    const info = this.getInfoRefs();
    if (!info) return null;

    const headRef = info.symrefs["HEAD"];
    const defaultBranchCommit = headRef
      ? info.refs[headRef]
      : Object.values(info.refs)[0];
    if (!defaultBranchCommit) return null;

    let offset = 0;
    let batchStart = defaultBranchCommit;

    while (offset < maxTotal) {
      if (signal.aborted) return null;

      const batch = await this.getCommitHistory(batchStart, batchSize, signal);
      if (!batch || batch.length === 0 || signal.aborted) return null;

      for (const commit of batch) {
        // Use committer timestamp (when the commit landed), falling back to
        // author timestamp if committer is somehow absent.
        const ts = commit.committer?.timestamp ?? commit.author.timestamp;
        if (ts <= targetTimestamp) {
          return commit.hash;
        }
      }

      offset += batch.length;

      // Walk to the first parent of the oldest commit in this batch.
      const tail = batch[batch.length - 1];
      if (tail.parents.length === 0) {
        // Reached the root commit — return it as the best we can do.
        return tail.hash;
      }
      batchStart = tail.parents[0];
    }

    return null; // exceeded maxTotal
  }

  /**
   * Fetch the data needed to compute a diff between two commits.
   *
   * Returns both commits and both complete recursive directory trees
   * (metadata only — no file content). The trees are fetched with
   * blob:none so only tree objects (~30 bytes per directory entry) are
   * transferred, not file content.
   *
   * Each fetchFullTree call returns both the commit and the tree from the
   * same packfile response, so no separate getSingleCommit requests are made.
   * Two fetches instead of four in the common (uncached) case.
   *
   * All fetches run in parallel and are individually cached.
   * Repeat calls for the same pair are instant.
   *
   * The caller is responsible for:
   *   1. Walking tipTree and baseTree to find added/deleted/modified paths
   *   2. Fetching changed file content via pool.getBlob()
   *   3. Generating unified diff output from the blob pairs
   *
   * @param fallbackUrls - Extra URLs to try after the pool's own URLs if the
   *   data is not found. Not tracked by the pool.
   */
  async getCommitRange(
    tipCommitId: string,
    baseCommitId: string,
    signal: AbortSignal,
    fallbackUrls?: string[],
  ): Promise<CommitRangeData | null> {
    const [tipResult, baseResult] = await Promise.all([
      this.withFallback(
        signal,
        async (url) => {
          const start = Date.now();
          const result = await this.http.fetchFullTree(
            url,
            tipCommitId,
            signal,
          );
          if (result) {
            const tracker = this.urlManager.get(url);
            tracker?.recordOperationSuccess(Date.now() - start);
          }
          return result;
        },
        fallbackUrls,
      ),
      this.withFallback(
        signal,
        async (url) => {
          const start = Date.now();
          const result = await this.http.fetchFullTree(
            url,
            baseCommitId,
            signal,
          );
          if (result) {
            const tracker = this.urlManager.get(url);
            tracker?.recordOperationSuccess(Date.now() - start);
          }
          return result;
        },
        fallbackUrls,
      ),
    ]);

    if (!tipResult || !baseResult) return null;

    return {
      tipCommit: tipResult.commit,
      baseCommit: baseResult.commit,
      tipTree: tipResult.tree,
      baseTree: baseResult.tree,
    };
  }

  /**
   * Get infoRefs for a specific URL (or the winner).
   * Primarily for UI components that need ref data.
   */
  getInfoRefs(url?: string): InfoRefsUploadPackResponse | null {
    const targetUrl = url ?? this.winnerUrl;
    if (!targetUrl) return null;
    const tracker = this.urlManager.get(targetUrl);
    return tracker?.state.infoRefs ?? null;
  }

  /**
   * Returns a merged infoRefs that unions refs from all URLs that have
   * successfully fetched infoRefs. The winner's symrefs and capabilities
   * take precedence; refs from all servers are merged so that refs only
   * present on some servers (e.g. GitHub-only branches) are visible.
   *
   * Winner refs take precedence in this raw union. Display consumers should
   * use getEffectiveInfoRefs(), which overlays the pool's per-ref resolution.
   */
  getMergedInfoRefs(): InfoRefsUploadPackResponse | null {
    const winner = this.getInfoRefs();
    const allTrackers = this.urlManager.getAll();
    const othersWithRefs = allTrackers
      .filter((t) => t.url !== this.winnerUrl && t.state.infoRefs !== null)
      .map((t) => t.state.infoRefs!);

    if (!winner && othersWithRefs.length === 0) return null;

    // Start from the winner (or first available) as the base
    const base = winner ?? othersWithRefs[0];
    if (othersWithRefs.length === 0) return base;

    // Merge: winner refs take precedence, then fill in from other servers
    const mergedRefs: Record<string, string> = {};
    // Add other servers' refs first (lower priority)
    for (const other of othersWithRefs) {
      for (const [name, hash] of Object.entries(other.refs)) {
        mergedRefs[name] = hash;
      }
    }
    // Winner's refs overwrite (higher priority)
    for (const [name, hash] of Object.entries(base.refs)) {
      mergedRefs[name] = hash;
    }

    return {
      ...base,
      refs: mergedRefs,
    };
  }

  /**
   * Return the merged ref advertisement with the shared display preference
   * applied per ref. Annotated tag object IDs are preserved while their
   * peeled commit entries receive the effective commit.
   */
  getEffectiveInfoRefs(): InfoRefsUploadPackResponse | null {
    const merged = this.getMergedInfoRefs();
    if (!merged) return null;

    const state = this.state$.getValue();
    const refs = { ...merged.refs };
    for (const [refName, resolved] of Object.entries(state.effectiveRefs)) {
      if (refs[`${refName}^{}`]) {
        const stateRef = this.stateManager.currentState?.refs.find(
          (ref) => ref.name === refName,
        );
        if (
          resolved.source === "state" &&
          stateRef &&
          !commitsMatch(stateRef.commitId, resolved.commitId)
        ) {
          // Legacy signed state used the annotated-tag object ID. Preserve
          // that signed raw ref while placing its normalized commit in ^{}.
          refs[refName] = stateRef.commitId;
        }
        refs[`${refName}^{}`] = resolved.commitId;
      } else {
        refs[refName] = resolved.commitId;
      }
    }

    let symrefs = merged.symrefs;
    if (state.viewSource !== "authoritative" && state.viewSource !== "nostr") {
      symrefs =
        this.urlManager.get(state.viewSource)?.state.infoRefs?.symrefs ??
        symrefs;
    } else {
      const headRef = this.stateManager.currentState?.headRef;
      if (headRef) symrefs = { ...symrefs, HEAD: headRef };
    }

    return { ...merged, refs, symrefs };
  }

  /**
   * Returns true if the given URL is currently being routed through the
   * CORS proxy. Useful for UI components that display proxy status.
   */
  urlUsesProxy(url: string): boolean {
    return this.cors.urlUsesProxy(url);
  }

  // -----------------------------------------------------------------------
  // Fallback execution
  // -----------------------------------------------------------------------

  /**
   * Execute an operation with the winning URL, falling back to other live
   * URLs on failure.
   *
   * @param fallbackUrls - Extra URLs to try after the pool's own ordered URLs
   *   if the operation returns null or throws. These URLs are not tracked by
   *   the pool — they are only used for this single operation invocation.
   *   Intended for PR/PR-Update clone URLs that may host commits not yet
   *   mirrored to the repo's main git servers.
   */
  private async withFallback<T>(
    signal: AbortSignal,
    operation: (url: string) => Promise<T | null>,
    fallbackUrls?: string[],
  ): Promise<T | null> {
    // Build ordered URL list: winner first, then other ok URLs by latency,
    // then any extra fallback URLs that aren't already in the pool.
    const poolUrls = this.getOrderedUrls();
    const poolUrlSet = new Set(poolUrls);
    const extraUrls = fallbackUrls
      ? fallbackUrls.filter((u) => !poolUrlSet.has(u) && !isNonHttpUrl(u))
      : [];
    const urls = [...poolUrls, ...extraUrls];

    if (urls.length === 0) return null;

    for (const url of urls) {
      if (signal.aborted) return null;
      try {
        const result = await operation(url);
        if (result !== null) {
          // If this is a pool-tracked URL that isn't the current winner,
          // consider promoting it.
          if (url !== this.winnerUrl && poolUrlSet.has(url)) {
            const newWinner = this.urlManager.selectBestUrl(
              this.winnerUrl ?? undefined,
            );
            if (newWinner !== this.winnerUrl) {
              this.winnerUrl = newWinner;
              this.setState((prev) => ({
                ...prev,
                winnerUrl: this.winnerUrl,
              }));
            }
          }
          return result;
        }
      } catch (err) {
        if (signal.aborted) return null;
        // Only record failures for pool-tracked URLs — extra fallback URLs
        // are ephemeral and should not affect pool health state.
        if (poolUrlSet.has(url)) {
          const tracker = this.urlManager.get(url);
          if (tracker) {
            const { errorClass, kind } = classifyFetchError(err);
            const msg = err instanceof Error ? err.message : String(err);
            if (errorClass === "permanent") {
              tracker.recordPermanentFailure(msg, kind);
            } else {
              tracker.recordTransientError(msg, kind);
            }
          }
        }
        // Continue to next URL
      }
    }

    return null;
  }

  /**
   * Get URLs in priority order: winner first, then other ok URLs sorted
   * by latency, then untested URLs.
   */
  private getOrderedUrls(): string[] {
    const all = this.urlManager.getAll();
    const result: string[] = [];

    // A concrete view override is a read-routing preference only. It never
    // changes authoritativeRefs or a write target, and normal fallbacks still
    // apply when the selected server lacks an object.
    const viewSource = this.state$.getValue().viewSource;
    const preferredViewUrl =
      viewSource !== "authoritative" && viewSource !== "nostr"
        ? viewSource
        : null;
    if (preferredViewUrl) {
      const preferred = this.urlManager.get(preferredViewUrl);
      if (preferred?.isUsable) result.push(preferredViewUrl);
    }

    // Winner first
    if (this.winnerUrl && this.winnerUrl !== preferredViewUrl) {
      const winner = this.urlManager.get(this.winnerUrl);
      if (winner && winner.isUsable) {
        result.push(this.winnerUrl);
      }
    }

    // Other ok URLs sorted by latency
    const okUrls = all
      .filter(
        (t) =>
          t.status === "ok" &&
          t.url !== this.winnerUrl &&
          t.url !== preferredViewUrl,
      )
      .sort((a, b) => a.avgLatency - b.avgLatency);
    for (const t of okUrls) {
      result.push(t.url);
    }

    // Untested URLs
    const untested = all.filter(
      (t) =>
        t.status === "untested" &&
        t.url !== this.winnerUrl &&
        t.url !== preferredViewUrl,
    );
    for (const t of untested) {
      result.push(t.url);
    }

    return result;
  }

  // -----------------------------------------------------------------------
  // Eviction / disposal
  // -----------------------------------------------------------------------

  private scheduleEviction(): void {
    this.evictTimer = setTimeout(() => {
      if (this.subscribers.size === 0) {
        this.dispose();
      }
    }, this.evictionGraceMs);
  }

  /**
   * Dispose the pool, cleaning up all resources.
   * Called automatically after the eviction grace period, or manually.
   */
  dispose(): void {
    this.abort?.abort();
    this.stateManager.cancelBackoff();
    this.stateEventSub?.unsubscribe();
    this.stateEventSub = null;
    if (this.evictTimer !== null) {
      clearTimeout(this.evictTimer);
      this.evictTimer = null;
    }
    this.subscribers.clear();
    this.state$.complete();
  }

  /** Whether this pool has been disposed */
  get isDisposed(): boolean {
    return this.state$.closed;
  }
}
